import React, { lazy, Suspense, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useImmer } from "use-immer";

import { IgDialog, IgSwitch, IgTypography } from "@intelligems/ig-theme";
import { Box, FormControlLabel, Grid, Skeleton } from "@mui/material";
import { useGetExperiences } from "@src/api/experiences/get-experiences";
import { useQueryClient } from "@tanstack/react-query";

import { useGetExperiencesList } from "../../api/experiences-list/get-experiences-list";
import { CheckoutScriptGenerator } from "../../api/formatters/checkoutScriptGenerator";
import { personalizationKeys } from "../../api/personalizations/use-dashboard-personalization-or-default";
import { getDialog, setDialog } from "../../store/slices/global";

const LazySyntaxHighlighter = lazy(
  // @ts-ignore
  async () => import("react-syntax-highlighter")
);

export const CheckoutScriptDialog = () => {
  const queryClient = useQueryClient();
  const dialog = useSelector(getDialog("checkoutScript"));

  const dispatch = useDispatch();
  const personalizationsList = useGetExperiencesList({
    category: "personalization",
  });

  const personalizations = useGetExperiences(
    personalizationsList?.data?.experiencesList?.map((e) => e.id) || []
  );

  const [showFullScript, setShowFullScript] = useImmer(false);
  const [code, setCode] = useImmer("");

  useEffect(() => {
    if (dialog.isOpen) {
      void queryClient.invalidateQueries({
        queryKey: personalizationKeys.all(true),
      });
    }
  }, [dialog.isOpen]);

  useEffect(() => {
    if (personalizations.every((p) => p.isSuccess)) {
      const generated = CheckoutScriptGenerator.generateSimpleScript(
        CheckoutScriptGenerator.buildCampaigns(
          personalizations.map((p) => p.data.experience)
        ),
        showFullScript
      );
      if (generated) setCode(generated);
    }
  }, [dialog.isOpen, personalizations, showFullScript]);

  const toggleShowFullScript = () => {
    setShowFullScript(!showFullScript);
  };

  const handleClose = () => {
    dispatch(setDialog({ dialog: "checkoutScript", open: false }));
  };

  if (!code) {
    return null;
  }

  const codeBlock = (
    <Grid item container>
      <Grid item sx={{ marginLeft: "7px" }}>
        <IgSwitch
          checked={showFullScript}
          color="primary"
          onClick={() => {
            toggleShowFullScript();
          }}
          sx={{ marginRight: "5px" }}
          label={"Show entire checkout script"}
        />
      </Grid>

      <Box
        sx={{
          textAlign: "left",
          padding: "0px 10px 0px 10px",
          borderRadius: "15px",
          maxHeight: "480px",
          overflowY: "scroll",
        }}
      >
        <Suspense fallback={<Skeleton variant="rectangular" height={480} />}>
          <LazySyntaxHighlighter
            language={"ruby"}
            showLineNumbers={true}
            useInlineStyles={true}
            startingLineNumber={showFullScript ? 1 : 338}
          >
            {code}
          </LazySyntaxHighlighter>
        </Suspense>
      </Box>
    </Grid>
  );

  const handleCopy = async () => {
    if (code) {
      await navigator.clipboard.writeText(code);
    }
  };

  return (
    <IgDialog
      showCloseIcon={true}
      title={"Checkout Script"}
      handleClose={handleClose}
      handleYesClick={() => {
        void handleCopy();
      }}
      rawContent={
        !personalizations.every((p) => p.isSuccess) ? (
          <Skeleton height={"500px"} />
        ) : (
          <Grid item container justifyContent={"start"} textAlign={"start"}>
            <Grid item>
              <IgTypography>
                You need to update your Checkout Script for your offer
                experiences. If you already have an Intelligems Experiences
                checkout script live, update your existing script using the
                partial script below. If not, paste the full script into a new
                or existing script.
              </IgTypography>
            </Grid>
            {codeBlock}
          </Grid>
        )
      }
      showDialog={dialog.isOpen}
      yesButtonText={"Copy Checkout Script"}
      loading={false}
      dialogHeight={null}
      noButtonText={"Close"}
      maxWidth={"xl"}
    />
  );
};
